import {useEffect, useMemo, useState} from 'react';
import classNames from 'classnames';

import Typography from 'components/Typography';
import Select from 'components/Select';

import {Driver, SelectOption, Vehicle} from 'types';

import location from '../../icons/location.svg';
import IconUser from '../../icons/user';
import IconUsers from '../../icons/users';
import styles from '../../RouteHistory.module.scss';
import localStyles from './AssignedHeader.module.scss';
import {CSSObjectWithLabel, SingleValue} from 'react-select';
import {GooglePlaceAutoComplete} from 'components/GooglePlaceAutoComplete';
import DateTimePicker from 'components/DateTimePicker';
import {parseJSON} from 'date-fns';
import Modal from 'components/Modal';
import DataTable from 'components/DataTable';
import {returnDrivers} from 'pages/Members/Teams/useTeams';
import {SUB_SCHEMA} from 'pages/DeliveryPlanner/DeliveryPlanner';
import ButtonWithIcon from 'components/ButtonWithIcon';
import {ChevronDownIcon} from 'pages/RouteHistory/icons/chevron-down';

interface Props {
  currentVehicle?: Vehicle;
  driver?: Driver;
  vehicles?: Vehicle[];
  startLocation: {
    name: string;
    lat: number;
    lng: number;
  };
  endLocation: {
    name: string;
    lat: number;
    lng: number;
  };
  onChange?: (
    startLocation: string,
    endLocation: string,
    currentVehicle?: Vehicle
  ) => void;
  onChangeStartTime?: (time: any) => void;
  start_time?: string | null;
  routes?: number;
  roundedTime: Date;
  drivers: any;
  isIndividual?: boolean;
}

const control = (base: CSSObjectWithLabel) => ({
  ...base,
  boxShadow: 'none',
  cursor: 'pointer',
  backgroundColor: '#fff',
});

const getUsername = (driver?: Driver) => {
  if (!driver) return '';
  return driver?.full_name || `${driver?.first_name} ${driver?.last_name}`;
};

function AssignedHeader({
  currentVehicle,
  vehicles,
  startLocation,
  endLocation,
  start_time,
  routes,
  roundedTime,
  drivers,
  isIndividual,
  driver,
}: Props) {
  const [showMemebers, setShowMembers] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number>();
  const [endpoints, setEndpoints] = useState({
    startLocation: {
      name: '',
      lat: 0,
      lng: 0,
    },
    endLocation: {
      name: '',
      lat: 0,
      lng: 0,
    },
  });

  const options = useMemo(
    function () {
      return vehicles?.map((v) => ({
        label: v.name,
        value: v.id,
      }));
    },
    [vehicles]
  );

  function onChangeVehicle(newValue: SingleValue<SelectOption>) {
    setSelectedVehicleId(Number(newValue?.value));
  }

  useEffect(
    function () {
      setSelectedVehicleId(Number(currentVehicle?.id));
      setEndpoints({
        startLocation,
        endLocation,
      });
    },
    [currentVehicle, startLocation, endLocation]
  );

  return (
    <>
      <div className={classNames(styles.panel__header)}>
        <div className={styles.panel__worker_container}>
          <div className={styles.workerIconBox}>
            {isIndividual ? (
              <IconUser width={22} height={22} />
            ) : (
              <IconUsers width={22} height={22} />
            )}
          </div>
          <div className={styles.panel__worker_container_text}>
            <Typography variant="h4" className={styles.name}>
              {isIndividual ? getUsername(driver) : currentVehicle?.name}
            </Typography>
            {(!!currentVehicle || isIndividual) && (
              <Typography style={{marginBottom: 0}} variant="label">
                Router: {routes} Jobs
              </Typography>
            )}
          </div>
        </div>
        <ButtonWithIcon
          icon={<ChevronDownIcon width={18} height={18} />}
          className={styles.assign_info}
          color="ghost"
          size="sm"
          onClick={() => setExpanded((prev) => !prev)}
        />
      </div>
      <div
        className={classNames(localStyles.form, {
          [localStyles['--expanded']]: expanded,
        })}
      >
        {expanded && (
          <div className={localStyles.form__content}>
            <div className={localStyles.formRow}>
              <div className={localStyles.label}>
                {isIndividual ? 'Worker:' : 'Team:'}
              </div>
              <div className={localStyles.team}>
                <Select
                  isDisabled
                  closeMenuOnSelect
                  styles={{control}}
                  menuIsOpen={false}
                  isSearchable={false}
                  options={options}
                  className={localStyles.select}
                  value={
                    isIndividual
                      ? {
                          label: `${driver?.first_name} ${driver?.last_name}`,
                          value: String(driver?.id),
                        }
                      : options?.find((o) => o.value === selectedVehicleId)
                  }
                  onChange={onChangeVehicle}
                />
              </div>
            </div>
            <div className={localStyles.formRow}>
              <div className={localStyles.label}>Start loc:</div>
              <div className={localStyles.root}>
                <div className={localStyles.icon_wrapper}>
                  <img alt="location" src={location} />
                </div>
                <GooglePlaceAutoComplete
                  wrapperClass={localStyles.wrapper}
                  style={{paddingLeft: 25}}
                  disabled
                  defaultValue={`${endpoints.startLocation.name}`}
                  placeholder="Search Address"
                  onPlaceSelected={(placeDetail) => {
                    setEndpoints((pr) => ({
                      ...pr,
                      startLocation: {
                        name: placeDetail.addressLine1,
                        lat: placeDetail.lat,
                        lng: placeDetail.lon,
                      },
                    }));
                  }}
                  options={{
                    types: ['address'],
                  }}
                />
              </div>
            </div>
            <div className={localStyles.formRow}>
              <div className={localStyles.label}>End loc:</div>
              <div className={localStyles.root}>
                <div className={localStyles.icon_wrapper}>
                  <img alt="location" src={location} />
                </div>
                <GooglePlaceAutoComplete
                  wrapperClass={localStyles.wrapper}
                  disabled
                  style={{paddingLeft: 25}}
                  placeholder="Search Address"
                  defaultValue={`${endpoints.endLocation.name}`}
                  onPlaceSelected={(placeDetail) => {
                    setEndpoints((pr) => ({
                      ...pr,
                      endLocation: {
                        name: placeDetail.addressLine1,
                        lat: placeDetail.lat,
                        lng: placeDetail.lon,
                      },
                    }));
                  }}
                  options={{
                    types: ['address'],
                  }}
                />
              </div>
            </div>
            <div className={localStyles.formRow}>
              <div className={localStyles.label}>Time:</div>
              <div className={localStyles.calendarRoot}>
                <DateTimePicker
                  disabled={true}
                  minTime={roundedTime}
                  maxTime={new Date(new Date().setHours(23, 59, 59))}
                  minDate={roundedTime}
                  value={start_time ? parseJSON(start_time) : undefined}
                  onChange={() => {}}
                />
              </div>
            </div>
            <div className={localStyles.actions}></div>
          </div>
        )}
      </div>
      <Modal
        onRequestClose={() => setShowMembers(false)}
        isOpen={showMemebers}
        onClose={() => setShowMembers(false)}
        className={localStyles.team_members_wrapper}
      >
        <Typography variant="h2">
          Team: {options?.find((o) => o.value === selectedVehicleId)?.label}
        </Typography>

        <Typography className={localStyles.membersTitle} variant="h4">
          Onsite Workers:
        </Typography>
        <DataTable
          containerStyle={localStyles.members}
          schema={SUB_SCHEMA}
          wrapperStyle={localStyles.membersWrapper}
          data={returnDrivers(drivers)}
        />
      </Modal>
    </>
  );
}

export default AssignedHeader;
